import { authHeaders } from './auth.js';
import { isRequired } from './consent.js';
/**
 * The chat session (ADR-0057): before chatting, the widget proves it is a
 * browser doing a little work — it finds the number behind a SHA-256
 * challenge from the RAG (a fraction of a second here, expensive for a
 * script asking thousands of times) and trades it for a short-lived token,
 * bound to this site and this visitor, sent with every message.
 *
 * Started in the background when the panel opens, so the first question
 * does not wait for it. Kept for the page in memory and sessionStorage
 * (this tab only); a token the RAG no longer accepts is replaced once.
 */

const STORAGE_PREFIX = 'chatbot_session:';
// Re-earn a little before the RAG's expiry, never mid-conversation.
const MARGIN_MS = 60 * 1000;

async function sha256Hex(text) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Finds the number; null if none in range (a challenge from someone else). */
export async function solve(challenge) {
  for (let number = 0; number <= challenge.maxnumber; number += 1) {
    if (await sha256Hex(`${challenge.salt}${number}`) === challenge.challenge) {
      return { ...challenge, number };
    }
  }
  return null;
}

function stored(siteKey) {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_PREFIX + siteKey) || 'null');
    return value && value.expiresAt - MARGIN_MS > Date.now() ? value.token : null;
  } catch {
    return null;
  }
}

function store(siteKey, token, expiresIn) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + siteKey, JSON.stringify({ token, expiresAt: Date.now() + expiresIn * 1000 }));
  } catch {
    // Storage blocked: the token lives for this page only.
  }
}

async function post(apiBase, path, body, headers = {}) {
  const response = await fetch(new URL(path, apiBase), {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...headers },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    // The chatbot asks for an authorization this visitor hasn't given (ADR-0089).
    if (isRequired(response.status, data)) throw new Error('consent_required');
    throw new Error(`session ${response.status}`);
  }
  return response.status === 204 ? {} : response.json();
}

/**
 * A session for this chatbot: `get()` resolves to a token, or null if one
 * could not be earned (the chat then tries without, and the RAG decides).
 * `getConsent()` gives the visitor's authorization (ADR-0089), sent with
 * every session it earns while it stands.
 */
export function chatSession(apiBase, siteKey, getConsent = () => null) {
  let pending = null;
  const earn = async () => {
    const challenge = await post(apiBase, '/api/session', { site_key: siteKey });
    const solution = await solve(challenge);
    if (!solution) throw new Error('unsolvable challenge');
    const consent = getConsent();
    const { token, expires_in: expiresIn } = await post(apiBase, '/api/session/verify', {
      site_key: siteKey,
      solution,
      ...(consent ? { consent: { id: consent.id, version: consent.version, ...(consent.locale ? { locale: consent.locale } : {}) } } : {}),
    });
    store(siteKey, token, expiresIn);
    return token;
  };
  return {
    get() {
      const token = stored(siteKey);
      if (token) return Promise.resolve(token);
      pending = pending || earn().catch(() => null).finally(() => { pending = null; });
      return pending;
    },
    forget() {
      try {
        sessionStorage.removeItem(STORAGE_PREFIX + siteKey);
      } catch {
        // Nothing stored.
      }
    },
  };
}

/** The visitor declined (ADR-0089): only counted. Fire and forget. */
export function declineConsent(apiBase, siteKey, version, locale) {
  return post(apiBase, '/api/consent/decline', { site_key: siteKey, version, ...(locale ? { locale } : {}) }).catch(() => null);
}

/** The visitor withdraws the authorization their session carries. */
export async function withdrawConsent(apiBase, siteKey, token) {
  if (!token) return false;
  try {
    await post(apiBase, '/api/consent/withdraw', { site_key: siteKey }, { 'X-Chat-Session': token });
    return true;
  } catch {
    return false;
  }
}
